import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { Skeleton } from '@material-ui/lab';
import { coachingStatuses, coachingTabs } from 'constants/ui';
import {
  useUserClassroomMisconceptions,
  userClassroomMisconceptionsGenerate,
  userClassroomMisconceptionsWarmup
} from 'sdk';

import { notifyError } from 'utils/notifications';

import styles from './styles.module.scss';

const CoachingContent = ({ setMisconceptionsLastUpdated }) => {
  const [isFailed, setIsFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(coachingTabs.WEEKLY);
  const [isWarmupComplete, setIsWarmupComplete] = useState(false);
  const [startPolling, setStartPolling] = useState(false);
  const [skippedTabs, setSkippedTabs] = useState({});
  const [now, setNow] = useState(Date.now());

  const processingTabRef = useRef(null);
  const isGeneratingRef = useRef(false);

  const {
    data: userMisconceptionsMovesData,
    isLoading: isUserClassroomMisconceptionsLoading,
    refetch: refetchUserClassroomMisconceptions
  } = useUserClassroomMisconceptions({
    params: { period: selectedTab.toLowerCase() }
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const cooldownPeriodPassed = useMemo(() => {
    const lastFetchTime = userMisconceptionsMovesData?.created_at;
    if (isUserClassroomMisconceptionsLoading) return false;
    if (!lastFetchTime) return true;

    const twelveMinutes = 12 * 60 * 1000;
    return now - new Date(lastFetchTime).getTime() > twelveMinutes;
  }, [
    userMisconceptionsMovesData?.created_at,
    isUserClassroomMisconceptionsLoading,
    now
  ]);

  const triggerInsightGeneration = useCallback(
    async (tab, isAutoRefresh) => {
      if (processingTabRef.current === tab && !isAutoRefresh) return;

      if (isGeneratingRef.current) return;

      processingTabRef.current = tab;
      isGeneratingRef.current = true;
      setIsLoading(true);
      setIsFailed(false);

      try {
        const response = await userClassroomMisconceptionsGenerate({
          period: tab.toLowerCase()
        });

        if (response?.data.status === coachingStatuses.SKIPPED) {
          setSkippedTabs((prev) => ({ ...prev, [tab.toLowerCase()]: true }));
          setStartPolling(false);
          setIsLoading(false);
          return;
        }

        setSkippedTabs((prev) => ({ ...prev, [tab.toLowerCase()]: false }));

        refetchUserClassroomMisconceptions();
        setStartPolling(true);
      } catch (error) {
        setIsFailed(true);
        notifyError('Could not generate new Insights data.');
        processingTabRef.current = null;
      } finally {
        setIsLoading(false);
        isGeneratingRef.current = false;
      }
    },
    [refetchUserClassroomMisconceptions]
  );

  useEffect(() => {
    if (userMisconceptionsMovesData?.created_at) {
      setMisconceptionsLastUpdated(userMisconceptionsMovesData.created_at);
    }
  }, [userMisconceptionsMovesData, setMisconceptionsLastUpdated]);

  useEffect(() => {
    if (!isWarmupComplete || isUserClassroomMisconceptionsLoading) return;

    const isGenerationIdle =
      userMisconceptionsMovesData?.generation_in_progress === false;
    const hasError =
      userMisconceptionsMovesData?.has_insights_generation_error === true;
    const isNewTab = processingTabRef.current !== selectedTab;

    if (isGenerationIdle && (isNewTab || cooldownPeriodPassed || hasError)) {
      if (isNewTab || hasError) {
        triggerInsightGeneration(selectedTab, false);
      } else if (cooldownPeriodPassed) {
        triggerInsightGeneration(selectedTab, true);
      }
    }
  }, [
    isWarmupComplete,
    cooldownPeriodPassed,
    userMisconceptionsMovesData?.generation_in_progress,
    userMisconceptionsMovesData?.has_insights_generation_error,
    isUserClassroomMisconceptionsLoading,
    selectedTab,
    triggerInsightGeneration
  ]);

  useEffect(() => {
    if (userMisconceptionsMovesData?.generation_in_progress === true) {
      setStartPolling(true);
    } else {
      setStartPolling(false);
    }
  }, [userMisconceptionsMovesData?.generation_in_progress]);

  useEffect(() => {
    userClassroomMisconceptionsWarmup();
    setIsWarmupComplete(true);
  }, []);

  useEffect(() => {
    if (userMisconceptionsMovesData?.generation_in_progress) {
      setStartPolling(true);
    }

    if (startPolling) {
      const interval = setInterval(() => {
        refetchUserClassroomMisconceptions();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [
    startPolling,
    userMisconceptionsMovesData?.generation_in_progress,
    refetchUserClassroomMisconceptions
  ]);

  const isTabSkipped = skippedTabs[selectedTab.toLowerCase()];

  const showSkeletons =
    (isLoading || isUserClassroomMisconceptionsLoading || startPolling) &&
    (!userMisconceptionsMovesData?.misconceptions ||
      userMisconceptionsMovesData.misconceptions.length === 0);

  const isEmpty =
    !showSkeletons &&
    !startPolling &&
    (isTabSkipped ||
      userMisconceptionsMovesData?.misconceptions?.length === 0 ||
      userMisconceptionsMovesData?.period !== selectedTab.toLowerCase());

  const showGlobalError =
    isFailed ||
    (!startPolling &&
      !isLoading &&
      userMisconceptionsMovesData?.has_insights_generation_error &&
      userMisconceptionsMovesData?.misconceptions?.length === 0);

  return (
    <div className={styles.coachingWidget}>
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${
            selectedTab === coachingTabs.DAILY ? styles.active : ''
          }`}
          onClick={() => setSelectedTab(coachingTabs.DAILY)}
        >
          Daily
        </button>
        <button
          className={`${styles.tab} ${
            selectedTab === coachingTabs.WEEKLY ? styles.active : ''
          }`}
          onClick={() => setSelectedTab(coachingTabs.WEEKLY)}
        >
          Weekly
        </button>
        <button
          className={`${styles.tab} ${
            selectedTab === coachingTabs.MONTHLY ? styles.active : ''
          }`}
          onClick={() => setSelectedTab(coachingTabs.MONTHLY)}
        >
          Monthly
        </button>
      </div>

      <div className={styles.cardList}>
        {showSkeletons ? (
          <div className={styles.loadingState}>
            <Skeleton variant="rect" width="100%" height={104} />
            <Skeleton variant="rect" width="100%" height={104} />
          </div>
        ) : showGlobalError ? (
          <div className={styles.errorState}>
            We encountered an issue while generating insights for your classroom
            misconception. Please try again later.
          </div>
        ) : isEmpty ? (
          <div className={styles.noInsightsContainer}>
            <p className={styles.noInsightsTitle}>
              No insights for the {selectedTab.toLowerCase()} period
            </p>
          </div>
        ) : (
          userMisconceptionsMovesData?.misconceptions?.map((item, index) => (
            <div key={item.name || index} className={styles.card}>
              <div className={styles.cardHeader}>
                <h5 className={styles.cardTitle}>{item.name}</h5>
              </div>
              <p className={styles.description}>{item.description}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CoachingContent;
